import React, { useState, useEffect, useRef } from "react";
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash/debounce';
import * as Styled from './SearchInputStyles';
import Form from "react-bootstrap/Form";
import search from "../../../images/search.svg";
import trovaPC from '../../../images/trova-PC.svg';
import puzzlePieces from '../../../images/puzzle-pieces.svg';
import warningIcon from '../../../images/warning-icon.svg';
import PromptModal from '../PromptModal/PromptModal';
import SearchStore from '../../xstores/search';
import useObservable from '../../hooks/useObservable';
import SubscriptionsStore from '../../xstores/subscriptions';
import { ReactComponent as ClearIcon } from '../../../images/close-icon.svg';
import Box from '../../styled/Box';
import {Image} from '../../styled/Image';
import { Paragraph } from '../../styled/Typography';
import auth from '../../services/authentication';

interface SearchInputProps {
  noMargin?: boolean;
}

const debouncedSuggestions = debounce((q:string) => {
  SearchStore.getSearchSuggestions(q);
}, 200);

const renderSuggestion = (suggestion:any, { query, isHighlighted }:any) => {
  return (
    <Styled.SearchSuggestion className={isHighlighted ? 'highlight': ''}>
      {suggestion}
    </Styled.SearchSuggestion>
  )
}

const renderSuggestionsContainer = ({containerProps, children, query}:any) => {
  return (
    <Styled.SuggestionsContainer {...containerProps}>
      {children &&
        <div className="inputDivider" />
      }
      {children}
    </Styled.SuggestionsContainer>
  )
}

const SearchInput = ({ noMargin }: SearchInputProps) => {
  const location = useLocation();
  const history = useHistory();
  const inputRef = useRef(null);
  const currentSearch = queryString.parse(location.search);
  const [searchValue, setSearchValue] = useState(currentSearch.q ? currentSearch.q.toString() : '')
  const [promptOpen, setPromptOpen] = useState(false);
  const [notLoggedInPrompt, setNotLoggedInPrompt] = useState(false);
  //const [searchSuggestions, setSearchSuggestions] = useState([]);

  const subscriptions = useObservable(SubscriptionsStore.current$, []);
  const searchSuggestions = useObservable(SearchStore.searchSuggestions$, []);
  const searchLoading = useObservable(SearchStore.loading$, false);

  const doSearch = async (e : any) => {
    e.stopPropagation();
    e.preventDefault();

    if (!searchValue.trim()) {
      setSearchValue('');
      return;
    }
    if (!auth.isAuthenticated()) {
      setNotLoggedInPrompt(true);
      return;
    }
    if (!subscriptions.length) {
      setPromptOpen(true);
      return;
    }
    SearchStore.setQuery(searchValue);
    history.push({
      pathname: '/searchresults',
      search: `?q=${encodeURIComponent(searchValue)}`
    });
  }
  const closePrompt = () => {
    setNotLoggedInPrompt(false);
    history.push('/account');
  }
  const closeLoginPrompt = () => {
    setPromptOpen(false);
    history.push('/signin-beta');
  }
  const updateSearch = (e:any, { newValue, method }:any) => {
    setSearchValue(newValue);
  }
  const getSuggestionValue = (val:any) => val

  const getSuggestions = ({value, reason}:any) => {
    if (reason === 'input-focused' || !subscriptions.length) {
      return;
    }
    const queryVal = value.trim();
    debouncedSuggestions(queryVal);
  }

  const clearSuggestions = () => {
    SearchStore.getSearchSuggestions('');
  }

  const selectSuggestion = (e:any, { suggestionValue }: any) => {
    setSearchValue(suggestionValue);
    SearchStore.setQuery(suggestionValue);
    history.push({
      pathname: '/searchresults',
      search: `?q=${encodeURIComponent(suggestionValue)}`
    });
  }

  const onClearSearch = () => {
    if (searchLoading) {
      return;
    }
    setSearchValue('');
    if (inputRef && inputRef.current) {
      // @ts-ignore
      inputRef.current.focus();
    }
    history.push({
      pathname: '/searchresults',
      search: ``
    });
  }

  useEffect(() => {
    let query = queryString.parse(location.search).q;
    if (query) {
      query = query.toString();
      setSearchValue(query);
      if (SearchStore.searchQuery$.getValue() !== query) {
        SearchStore.setQuery(query);
      }
    }
  }, [location.search]);

  return (
    <Styled.Container className={noMargin ? 'mb-0 ml-auto mr-auto' : ''}>
      <Form inline style={{ flex: 1 }} onSubmit={doSearch} >
        <Styled.StyledFormGroup controlId="formSearch" >
          <Autosuggest
            suggestions={searchSuggestions}
            getSuggestionValue={getSuggestionValue}
            inputProps={{
              value: searchValue,
              onChange: updateSearch,
            }}
            containerProps={{
              className: 'inputContainer'
            }}
            onSuggestionsFetchRequested={getSuggestions}
            onSuggestionsClearRequested={clearSuggestions}
            onSuggestionSelected={selectSuggestion}
            shouldRenderSuggestions={(v) => v.length > 0}
            renderSuggestion={renderSuggestion}
            renderInputComponent={(inputProps) => (
              <Styled.InputContainer>
                <Styled.StyledFormControl
                  {...inputProps}
                  ref={inputRef}
                  type="text"
                  placeholder="Search"
                  className='SearchField'
                  disabled={searchLoading}
                />
                {!!searchValue.length && (
                  <Styled.ClearSearch
                    onClick={onClearSearch}
                    className='ResultsModalClose'
                    variant='link'
                  >
                    <ClearIcon />
                  </Styled.ClearSearch>
                )}
              </Styled.InputContainer>
            )}
            renderSuggestionsContainer={renderSuggestionsContainer}
          />
          <Styled.SearchButton
            className='SearchButton'
            disabled={searchLoading}
            type="submit"
          >
            <img src={search} alt="Search logo"/>
          </Styled.SearchButton>
        </Styled.StyledFormGroup>
      </Form>
      <PromptModal
        open={notLoggedInPrompt}
        message={(
          <>
            <Box display={'flex'} justifyContent={'center'} mb={4}>
              <Image src={trovaPC} />
            </Box>
            <Paragraph mb={0} color={'primaryViolet'}>
              To start searching, please sign in to your Trova account. Thanks!
            </Paragraph>
          </>
        )}
        onClose={closeLoginPrompt}
        buttonTxt="Sign In"
      />
      <PromptModal
        open={promptOpen}
        message={(
          <>
            <Box display={'flex'} justifyContent={'center'} mb={4}>
              <Image src={puzzlePieces} />
            </Box>
            <Styled.StyledParagraph>
              To begin with Trova, connect your apps and search content across your workspace(s).
            </Styled.StyledParagraph>
            <img src={warningIcon} alt="Warning"/>
            <Styled.StyledSpan>
              All of your data is encrypted.
            </Styled.StyledSpan>
          </>
        )}
        onClose={closePrompt}
        buttonTxt="Connect account"
      />
    </Styled.Container>
  )
}

export default SearchInput;